import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Input,
  List,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, FlagFilled } from '@ant-design/icons';
import { APIClient, getLoggedinUser } from '../helpers/api_helper';

const { Text } = Typography;
const { TextArea } = Input;
const api = new APIClient();

const formatTs = (ts) => (ts ? new Date(ts).toLocaleString('sl-SI') : '');

const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const IntentComments = ({ intentId, isFlagged, onFlagChange, onCountChange }) => {
  const currentUserId = getLoggedinUser()?.id ?? null;

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [flagBusy, setFlagBusy] = useState(false);

  const fetchComments = () => {
    if (!intentId) return;
    setLoading(true);
    api
      .get(`/api/intents/${intentId}/comments/`)
      .then((resp) => {
        const list = resp || [];
        setComments(list);
        onCountChange?.(list.length);
      })
      .catch((err) => console.error('Comments fetch error:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchComments();
  }, [intentId]);

  const handleAdd = () => {
    const text = newText.trim();
    if (!text) return;
    setPosting(true);
    api
      .create(`/api/intents/${intentId}/comments/`, { text }, { successMsg: null })
      .then(() => {
        setNewText('');
        fetchComments();
      })
      .catch((err) => console.error('Comment create error:', err))
      .finally(() => setPosting(false));
  };

  const handleStartEdit = (c) => {
    setEditingId(c.id);
    setEditingText(c.text);
  };

  const handleSaveEdit = (c) => {
    const text = editingText.trim();
    if (!text) return;
    api
      .update(
        `/api/intents/${intentId}/comments/${c.id}/`,
        { text },
        { successMsg: null },
      )
      .then(() => {
        setEditingId(null);
        setEditingText('');
        fetchComments();
      })
      .catch((err) => console.error('Comment update error:', err));
  };

  const handleDelete = (c) => {
    api
      .delete(`/api/intents/${intentId}/comments/${c.id}/`, null, { successMsg: null })
      .then(() => fetchComments())
      .catch((err) => console.error('Comment delete error:', err));
  };

  const handleFlagToggle = (checked) => {
    setFlagBusy(true);
    api
      .update(`/api/intents/${intentId}/`, { is_flagged: checked }, { successMsg: null })
      .then(() => {
        onFlagChange?.(checked);
      })
      .catch((err) => console.error('Flag toggle error:', err))
      .finally(() => setFlagBusy(false));
  };

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Alert
          type={isFlagged ? 'error' : 'info'}
          showIcon
          icon={<FlagFilled style={{ color: isFlagged ? '#ff4d4f' : undefined }} />}
          message={
            <Space>
              <span>{isFlagged ? 'Transakcija je označena za pozornost.' : 'Transakcija ni označena.'}</span>
              <Switch
                size="small"
                checked={!!isFlagged}
                loading={flagBusy}
                onChange={handleFlagToggle}
                checkedChildren="označeno"
                unCheckedChildren="neoznačeno"
              />
            </Space>
          }
        />

        {comments.length === 0 ? (
          <Empty description="Brez komentarjev" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={comments}
            renderItem={(c) => {
              const isAuthor = currentUserId != null && c.created_by === currentUserId;
              const isEditing = editingId === c.id;
              return (
                <List.Item
                  actions={
                    isAuthor && !isEditing
                      ? [
                          <Button
                            key="edit"
                            size="small"
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleStartEdit(c)}
                          />,
                          <Popconfirm
                            key="del"
                            title="Izbrišem komentar?"
                            okText="Izbriši"
                            cancelText="Prekliči"
                            onConfirm={() => handleDelete(c)}
                          >
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                          </Popconfirm>,
                        ]
                      : undefined
                  }
                >
                  <List.Item.Meta
                    avatar={<Avatar>{initialsOf(c.created_by_username)}</Avatar>}
                    title={
                      <Space size={8}>
                        <Text strong>{c.created_by_username || '—'}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatTs(c.time_created)}
                        </Text>
                        {c.time_edited && (
                          <Text type="secondary" style={{ fontSize: 12 }} italic>
                            (urejeno {formatTs(c.time_edited)})
                          </Text>
                        )}
                      </Space>
                    }
                    description={
                      isEditing ? (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <TextArea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            autoSize={{ minRows: 2, maxRows: 8 }}
                          />
                          <Space>
                            <Button size="small" type="primary" onClick={() => handleSaveEdit(c)}>
                              Shrani
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                setEditingId(null);
                                setEditingText('');
                              }}
                            >
                              Prekliči
                            </Button>
                          </Space>
                        </Space>
                      ) : (
                        <Text style={{ whiteSpace: 'pre-wrap' }}>{c.text}</Text>
                      )
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}

        <Space direction="vertical" style={{ width: '100%' }}>
          <TextArea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Napiši komentar…"
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
          <Button
            type="primary"
            disabled={!newText.trim() || posting}
            loading={posting}
            onClick={handleAdd}
          >
            Dodaj komentar
          </Button>
        </Space>
      </Space>
    </Spin>
  );
};

export default IntentComments;
